import React from 'react'
import ReactDOM from 'react-dom'
import App from './App'
import * as serviceWorker from './serviceWorker'
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import client from './apolloClient'

const getAllowedHostSuffixes = () => {
  // Comma-separated apex domains (e.g. "investhub.io,equanimity.li").
  // If set, overrides defaults.
  const raw = process.env.REACT_APP_SENTRY_ALLOWED_HOSTS;
  if (raw) {
    return raw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
  }

  // Default allowlist: only send events for our known tenant domains.
  // This prevents unrelated deployments (e.g. old forks) from polluting our Sentry project.
  return [
    'investhub.io',
    'investhub.org',
    'enercom.io',
    'enercom.ag',
    'blockchainmaji.com',
    'equanimity.li',
    'awearetech.li',
    'maji.capital',
    'ixo.world',
    'cryptoadvisory.li',
    'calidrisfintech.com',
    'calidris-fintech.ch',
    'musicworldcup.com',
    'cll.li',
    // keep for legacy hostname visibility if still routed to us
    'bitwala.com',
  ];
};

const isAllowedSentryHost = () => {
  if (typeof window === 'undefined') return false;
  const hostname = String(window.location.hostname || '').toLowerCase();
  if (!hostname) return false;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;

  const allowSuffixes = getAllowedHostSuffixes();
  return allowSuffixes.some((suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`));
};

const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
if (sentryDsn && isAllowedSentryHost()) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [new BrowserTracing()],

    // We recommend adjusting this value in production
    tracesSampleRate: 0,

    beforeSend(event) {
      try {
        // Hard gate: never accept events from non-tenant hostnames.
        if (!isAllowedSentryHost()) {
          return null;
        }

        const exception = event && event.exception && event.exception.values && event.exception.values[0];
        const value = String((exception && exception.value) || event.message || '');

        // Drop common non-actionable frontend noise:
        // - transient network failures (offline, blocked third-party scripts)
        // - backend 5xx surfacing as Apollo "Network error"
        if (
          value.includes('Network error: Failed to fetch') ||
          value.includes('Network error: Response not successful') ||
          value.includes('LogRocket: script could not load')
        ) {
          return null;
        }
      } catch (_e) {
        // ignore
      }

      return event;
    },
  });
}

ReactDOM.render(<App apolloClient={client} />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
